
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { createUser } from '@/services/firebase';
import { sendSignupData, resendVerificationCode } from '@/services/api';

const VerifyCode = () => {
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userData, setUserData] = useState<any>(null);
  const [countdown, setCountdown] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const [isInIframe, setIsInIframe] = useState(false);
  
  useEffect(() => {
    const storedData = localStorage.getItem('verificationData');
    
    if (!storedData) {
      toast.error("Verification data not found");
      navigate('/');
      return;
    }
    
    try {
      const parsedData = JSON.parse(storedData);
      console.log("Loaded verification data:", parsedData);
      
      // Ensure phone is numeric only - strip ALL non-numeric characters
      if (parsedData.phone) {
        parsedData.phone = parsedData.phone.replace(/\D/g, '');
        console.log("Formatted phone:", parsedData.phone);
      }
      
      setUserData(parsedData);
    } catch (error) {
      console.error("Error parsing verification data:", error);
      toast.error("Invalid verification data");
      navigate('/');
      return;
    }
    
    // Check if running in iframe
    setIsInIframe(window.top !== window.self);
    console.log("Is in iframe:", window.top !== window.self);
    
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          setCanResend(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    // Log domain information for debugging
    console.log(`Running on domain: ${window.location.hostname}`);
    console.log(`Is production domain: ${window.location.hostname === 'refer.solarinphoenix.com'}`);
    
    return () => clearInterval(timer);
  }, [navigate]);
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!userData) {
      toast.error("No user data found");
      return;
    }
    
    // Log values for debugging
    console.log("Entered code:", code);
    console.log("Expected code:", userData.verificationCode);
    
    // Fix: Convert both codes to strings and trim any whitespace before comparing
    const enteredCode = code.trim();
    const expectedCode = String(userData.verificationCode).trim();
    
    if (enteredCode !== expectedCode) {
      toast.error("Invalid verification code");
      return;
    }
    
    try {
      setIsSubmitting(true);
      
      // Make sure phone is correctly formatted before sending
      if (userData.phone) {
        userData.phone = userData.phone.replace(/\D/g, '');
      }
      
      // Send signup data to webhook first
      console.log("Sending signup data to webhook...");
      await sendSignupData(userData);
      console.log("Signup data sent successfully");
      
      // Prepare user data for Firebase without the verification code
      const { verificationCode, ...userDataForFirebase } = userData;
      
      // Try to create Firebase user
      console.log("Creating Firebase user...");
      try {
        await createUser(userDataForFirebase);
        console.log("Firebase user created successfully");
        
        // Store referrer code for future use
        localStorage.setItem('referrerCode', userData.referrerCode);
        
        // Clean up verification data
        localStorage.removeItem('verificationData');
        
        // If in iframe, send message to parent window
        if (isInIframe) {
          try {
            window.parent.postMessage({ 
              type: 'signup-success', 
              userData: {
                email: userData.email,
                referrerCode: userData.referrerCode
              } 
            }, '*');
          } catch (error) {
            console.error("Error posting message to parent:", error);
          }
        }
        
        // Navigate to dashboard
        navigate('/dashboard');
        
        toast.success("Account created successfully");
      } catch (firebaseError) {
        console.error("Firebase user creation error:", firebaseError);
        
        // Even if Firebase user creation fails, we can still consider the account created
        // since the data was already sent to the webhook
        toast.warning("Account created but sign-in is not available. Please contact support.");
        
        // Clean up verification data
        localStorage.removeItem('verificationData');
        
        // If in iframe, send message to parent window about partial success
        if (isInIframe) {
          try {
            window.parent.postMessage({ 
              type: 'signup-partial', 
              userData: {
                email: userData.email,
                referrerCode: userData.referrerCode
              } 
            }, '*');
          } catch (error) {
            console.error("Error posting message to parent:", error);
          }
        }
        
        // Navigate to index page
        navigate('/');
      }
    } catch (error) {
      console.error("Verification error:", error);
      toast.error("Failed to verify account. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleResendCode = async () => {
    try {
      if (!userData) return;
      
      setCanResend(false);
      setCountdown(60);
      
      const timer = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            setCanResend(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
      const newCode = Math.floor(1000 + Math.random() * 9000).toString();
      
      const updatedData = { ...userData, verificationCode: newCode };
      localStorage.setItem('verificationData', JSON.stringify(updatedData));
      setUserData(updatedData);
      
      // Ensure phone is numeric only before sending
      const formattedUserData = {
        ...userData,
        phone: userData.phone.replace(/\D/g, ''),
      };
      
      await resendVerificationCode(formattedUserData, newCode);
      
      toast.success("Verification code resent");
    } catch (error) {
      console.error("Resend error:", error);
      toast.error("Failed to resend code. Please try again.");
      setCanResend(true);
    }
  };
  
  return (
    <div className="w-full max-w-md mx-auto animate-fadeIn">
      <Card className="glass-panel">
        <CardHeader>
          <CardTitle className="text-2xl font-bold text-center">Verify Your Account</CardTitle>
          <CardDescription className="text-center text-base">
            Enter the 4-digit code sent to your phone
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <label htmlFor="code" className="text-sm font-medium">
                Verification Code
              </label>
              <Input
                id="code"
                name="code"
                placeholder="Enter 4-digit code"
                maxLength={4}
                required
                className="input-style text-center text-lg tracking-widest"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, ''))}
              />
            </div>
            
            <Button
              type="submit"
              className="w-full h-12 glass-button text-lg"
              disabled={isSubmitting || code.length !== 4}
            >
              {isSubmitting ? "Verifying..." : "Verify Code"}
            </Button>
            
            <div className="text-center mt-4">
              <p className="text-sm text-muted-foreground mb-2">
                Didn't receive a code?
              </p>
              {canResend ? (
                <Button
                  type="button"
                  variant="link"
                  className="text-accent hover:text-accent/80"
                  onClick={handleResendCode}
                >
                  Resend Code
                </Button>
              ) : (
                <p className="text-sm">
                  Resend code in <span className="text-accent">{countdown}s</span>
                </p>
              )}
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default VerifyCode;
